import { typeUtil, urlUtil, logUtil, objUtil } from "@repo/lib-common";


import { StorageManager } from "@repo/lib-browser-data-store";
import { AffiliateParams, validateAffliateParams } from "./types/affiliateParams";
import { ClientConfig, validateClientConfig } from "./types/clientConfig";
import { PurchaseOrderTransaction } from "./types/purchaseOrderTransaction";
import { PurchaseOrder, validatePurchaseOrder } from "./types/purchaseOrder";
import { StoreData } from "./types/purchaseOrderSimple";
import { httpUtil } from "@repo/lib-web";

logUtil.setLogPrefix("AdPartners");

const prodApiUrl = "https://winfluenced-pixel-bs56cx2ajq-ey.a.run.app";
const testApiUrl = "https://winfluenced-pixel-rvlaoxgvjq-ey.a.run.app";
//const localApiUrl = "http://localhost:8080";
const apiUrl = `${import.meta.env.PROD ? prodApiUrl : testApiUrl
    }/api/v2-pixel/ecomm`;

class AdpEcommClientV2 {
    private readonly v = "2.0";
    private readonly storageAdpKey = "_adp_v2";
    private readonly storageMixModelAdpKey = "_adp_v2_orders";

    private readonly offerIdQueryStringKey = "adp_oid";
    private readonly transactionClickIdQueryStringKey = "adp_cid";
    private readonly utmCampaignQueryStringKey = "utm_campaign";
    private readonly utmMediumQueryStringKey = "utm_medium";
    private readonly utmSourceQueryStringKey = "utm_source";
    private readonly utmContentQueryStringKey = "utm_content";
    private readonly utmTermQueryStringKey = "utm_term";

    private readonly queryStringParams = [
        this.offerIdQueryStringKey,
        this.transactionClickIdQueryStringKey,
        this.utmCampaignQueryStringKey,
        this.utmMediumQueryStringKey,
        this.utmSourceQueryStringKey,
        this.utmContentQueryStringKey,
        this.utmTermQueryStringKey,
    ];

    private attributionWindowInDays = 14;

    public setAttributionWindow(days: number) {
        if (days > 180) {
            logUtil.consoleError("attribution window must be less than 180 days");
        }
        this.attributionWindowInDays = days;
    }

    public get storageAdpKeyTTLinMinutes() {
        return this.attributionWindowInDays * 24 * 60;
    }

    private config: ClientConfig | undefined;
    private affiliateParams: AffiliateParams | undefined;
    private eventApiUrl: string = apiUrl;
    private purchaseOrders = new Array<PurchaseOrder>();
    private purchaseOrderTransactions = new Array<PurchaseOrderTransaction>();

    private state_IsInitForTransactionCompleted = false;
    private state_IsInitForTrackingCompleted = false;
    private state_IsEnsureAppDataCompleted = false;
    private state_IsAffiliateQueryStringParamsExemined = false;
    private state_IsAffiliateParamsSaved = false;
    private state_IsAffiliateParamsLoaded = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public states(): any {

        const _states = {
            state_IsInitForTrackingCompleted: this.state_IsInitForTrackingCompleted,
            state_IsInitForTransactionCompleted: this.state_IsInitForTransactionCompleted,

            state_IsEnsureAppDataCompleted: this.state_IsEnsureAppDataCompleted,
            state_IsAffiliateQueryStringParamsExemined:
                this.state_IsAffiliateQueryStringParamsExemined,
            state_IsAffiliateParamsSaved: this.state_IsAffiliateParamsSaved,
            state_IsAffiliateParamsLoaded: this.state_IsAffiliateParamsLoaded,
        };
        logUtil.consoleLog(`version${this.v} client states`, _states)
        return _states;
    }

    public initForTracking() {
        this.registerReadyStateChange();
        this.state_IsInitForTrackingCompleted = true;
    }

    public initForTransaction(config?: Partial<ClientConfig>) {
        if (objUtil.isObjNullOrUndefined(config) === true || (validateClientConfig(config) !== true)) {
            logUtil.consoleWarn(
                "config or config properties can not be null or empty "
            );
            return;
        }
        this.config = new ClientConfig(config);
        this.registerReadyStateChange();
        logUtil.consoleLog("client config:", this.config);
        this.state_IsInitForTransactionCompleted = true;
        this.loadAffiliateParamsFromStorage();
    }

    public ensureAdpData() {
        if (this.state_IsAffiliateQueryStringParamsExemined) return;
        this.state_IsAffiliateQueryStringParamsExemined = true;
        const adpUrlParams = urlUtil.getUrlQueryStringValues(this.queryStringParams);
        logUtil.consoleLog("adpUrlParams found", adpUrlParams);
        const adpTransactionClickIdUrlDataValue = adpUrlParams.get(
            this.transactionClickIdQueryStringKey
        );
        const adpOfferIdDataValue = adpUrlParams.get(this.offerIdQueryStringKey);
        const isMissing_adpTransactionClickIdUrlDataValue = objUtil.isNullOrEmpty(
            adpTransactionClickIdUrlDataValue
        );
        const isMissing_adpOfferIdDataValue = objUtil.isNullOrEmpty(adpOfferIdDataValue);

        if (isMissing_adpTransactionClickIdUrlDataValue) {
            logUtil.consoleLog("query string apd_cid is missing");
            return;
        }
        if (isMissing_adpOfferIdDataValue) {
            logUtil.consoleLog("query string apd_cid is missing");
            return;
        }

        this.affiliateParams = new AffiliateParams();
        this.affiliateParams.referrer = urlUtil.getDocumentReferrer();
        this.affiliateParams.eventId = objUtil.uuidStr();
        this.affiliateParams.offerId = adpOfferIdDataValue;
        this.affiliateParams.transactionId = adpTransactionClickIdUrlDataValue;
        this.affiliateParams.utmCampaign = adpUrlParams.get(
            this.utmCampaignQueryStringKey
        );
        this.affiliateParams.utmSource = adpUrlParams.get(
            this.utmSourceQueryStringKey
        );
        this.affiliateParams.utmMedium = adpUrlParams.get(
            this.utmMediumQueryStringKey
        );
        this.affiliateParams.utmContent = adpUrlParams.get(
            this.utmContentQueryStringKey
        );
        this.affiliateParams.utmTerm = adpUrlParams.get(this.utmTermQueryStringKey);
        this.saveAffiliateParamsToStorage();

        this.state_IsEnsureAppDataCompleted = true;
    }

    private registerReadyStateChange(
        completeReadyStateCallBack?: typeUtil.CallbackFunction,
        interactiveStateCallBack?: typeUtil.CallbackFunction
    ) {
        //check if readyState is already complete -->

        if (document.readyState === "complete") {
            this.ensureAdpData();
            if (interactiveStateCallBack) {
                interactiveStateCallBack();
                logUtil.consoleLog(
                    `${interactiveStateCallBack.name} callback is triggered`
                );
            }
            if (completeReadyStateCallBack) {
                completeReadyStateCallBack();
                logUtil.consoleLog(
                    `${completeReadyStateCallBack.name} callback is triggered`
                );
            }
            return;
        }
        //check if readyState is already complete -->

        document.addEventListener("readystatechange", (_event) => {
            logUtil.consoleLog(`document.readyState became ${document.readyState}`);
            if (document.readyState === "interactive") {
                this.ensureAdpData();
                if (interactiveStateCallBack) {
                    interactiveStateCallBack();
                    logUtil.consoleLog(
                        `${interactiveStateCallBack.name} callback is triggered`,
                        _event
                    );
                }
            } else if (document.readyState === "complete") {
                this.ensureAdpData();
                if (completeReadyStateCallBack) {
                    completeReadyStateCallBack();
                    logUtil.consoleLog(
                        `${completeReadyStateCallBack.name} callback is triggered`,
                        _event
                    );
                }
            }
        });
    }

    private saveAffiliateParamsToStorage(): void {
        const data = this.affiliateParams;
        if (data) {
            StorageManager.save<AffiliateParams>(
                this.storageAdpKey,
                data,
                this.storageAdpKeyTTLinMinutes
            );
            this.state_IsAffiliateParamsSaved = true;
            logUtil.consoleLog("affiliate params saved", data);
        } else {
            logUtil.consoleLog("affiliate params are undefined and not saved ");
        }
    }

    private loadAffiliateParamsFromStorage(): boolean {
        const data = StorageManager.load<AffiliateParams>(this.storageAdpKey);
        this.affiliateParams = data;
        if (data) {
            this.state_IsAffiliateParamsLoaded = true;
            logUtil.consoleLog("affiliate params loaded", data);
            return true;
        } else {
            logUtil.consoleLog("affiliate params not found");
            return false;
        }
    }

    public isAffiliateAvailable(): boolean {
        const isDataFound = this.loadAffiliateParamsFromStorage();
        if (isDataFound !== true) {
            return false;
        }
        if (objUtil.isObjNullOrUndefined(this.affiliateParams)) {
            return false;
        }
        if (objUtil.isNullOrEmpty(this.affiliateParams?.offerId)) {
            return false;
        }
        if (objUtil.isStrNullOrUndefined(this.affiliateParams?.transactionId)) {
            return false;
        }
        return true;

    }

    private _send(purchaseOrderData: PurchaseOrder) {
        //data validations -->
        const isDataFound = this.loadAffiliateParamsFromStorage();
        if (!isDataFound) {
            logUtil.consoleWarn(
                "affiliate params not found, transaction canceled"
            );
            return null;
        }
        const orderNumber = purchaseOrderData.orderNumber || "N/A";
        const dataWrapper = StorageManager.load<StoreData>(this.storageMixModelAdpKey);
        if (dataWrapper && dataWrapper?.orders) {
            const x = dataWrapper.orders.some(q => q === orderNumber);
            if (x) {
                logUtil.consoleError("order is already placed and conversion/transaction saved");
                return null;
            }
        }

        if (validateClientConfig(this.config) !== true) {
            logUtil.consoleError("client config is not valid, please check data");
            return;
        }
        logUtil.consoleLog("affParams", this.affiliateParams);
        if (validateAffliateParams(this.affiliateParams) !== true) {
            logUtil.consoleError("affiliate params are not valid, please check data");
            return;
        }
        if (validatePurchaseOrder(purchaseOrderData) !== true) {
            logUtil.consoleError("purchase order is not valid, please check data");
            return;
        }
        //data validations <--

        const transaction = new PurchaseOrderTransaction({
            purchaseOrder: purchaseOrderData,
            affiliateParams: this.affiliateParams!,
            clientConfig: this.config!,
        });

        this.purchaseOrders.push(purchaseOrderData);
        this.purchaseOrderTransactions.push(transaction);
        logUtil.consoleLog("transaction process starts");
        logUtil.consoleLog("transaction request", transaction);






        const postObjData = transaction.toApiObject();
        logUtil.consoleLog("transactipn api object data", postObjData);
        httpUtil.httpPost({
            url: this.eventApiUrl, data: postObjData,
            contentType: httpUtil.ContentTypeEnum.json,
            responseCallBack: (response) => {
                let dataWrapper = StorageManager.load<StoreData>(this.storageMixModelAdpKey);
                dataWrapper = dataWrapper || new StoreData();
                dataWrapper.orders.push(orderNumber);
                StorageManager.save<StoreData>(this.storageMixModelAdpKey, dataWrapper, this.storageAdpKeyTTLinMinutes)
                logUtil.consoleLog("transaction response", response);
            }
        })

    }

    public send(data: PurchaseOrder) {
        this._send(data);
    }

    public sendPurchaseOrder(data: PurchaseOrder) {
        if (data) {
            this?.send(data);
            return;
        }
        logUtil.consoleError("order is undefined");
    }
}

export { AdpEcommClientV2 as AdpEcommClient };
